import { ReactNode } from 'react';

import { Trans } from '@lingui/react/macro';

import styles from './StepLayout.module.css';
import { Button } from '../../Button/Button';

type TStepLayoutProps = {
  stageTitle: string;
  heading: string;
  subHeading?: string;
  children: ReactNode;
  /** Defaults to the localised "Next step". ReactNode so it can hold a <Trans>. */
  ctaLabel?: ReactNode;
  ctaDisabled?: boolean;
  /** Hide the CTA — for screens that advance on selection (e.g. card pickers). */
  hideCta?: boolean;
  onContinue?: () => void;
};

/**
 * The per-step content frame used by each screen: stage title, heading,
 * sub-heading, the screen's content, and the sticky CTA. Composed inside a
 * screen so it sits in the animating region (0.6). The screen owns its form and
 * passes CTA validity in via `ctaDisabled`.
 */
export const StepLayout = ({
  stageTitle,
  heading,
  subHeading,
  children,
  ctaLabel,
  ctaDisabled = false,
  hideCta = false,
  onContinue,
}: TStepLayoutProps) => (
  <div className={styles.step}>
    <p className={styles.stageTitle}>{stageTitle}</p>
    <h1 className={styles.heading} tabIndex={-1} data-step-heading>
      {heading}
    </h1>
    {subHeading && <p className={styles.subHeading}>{subHeading}</p>}

    <div className={styles.content}>{children}</div>

    {!hideCta && (
      <footer className={styles.footer}>
        <Button
          variant="primary"
          size="large"
          disabled={ctaDisabled}
          onClick={onContinue}
          className={styles.cta}
        >
          {ctaLabel ?? <Trans>Next step</Trans>}
        </Button>
      </footer>
    )}
  </div>
);
